import React from "react";
import styles from "./FHIRPatient.module.scss";
import DateTimePicker from "react-datetime-picker";
import { capitalize } from "../../utils/textFunctions";
import RecordOverrideOptions from "../../routes/NewSearchRecords/SearchRecordTable/RecordOverrideOptions";
import {
  overrideSummaryForTelecom,
  listOfEmailTypes,
  listOfPhoneTypes
} from "../../routes/NewSearchRecords/SearchRecordTable/RecordOverrideUtil";
import { handleMultipleAttributeOverride } from "../../routes/NewSearchRecords/SearchRecordTable/MultipleAttributeOverride/Utils";
import { formattedDateWithDefault } from "../../utils/formattedDate";
import Modal from "../../components/ModalPopup";
import ButtonList from "../../components/ButtonList";
import Button from "../../components/Button";
import LinkButton from "../../components/LinkButton";
import OverrideAuditInputModal from "../../routes/NewSearchRecords/SearchRecordTable/OverrideAuditInputModal";
import { renderFieldWithDiff } from "./Utils";

const Telecom = ({
  data,
  summary: isSummary,
  oncRecordOverrideIconClick,
  className,
  isInvisible = false,
  isAggregateInfo = false,
  isImdm = false,
  isNewQuarantine = false,
  isMultipleAttributeOverride = false,
  setAttributeOverrides,
  attributeOverrides,
  hasDiff = false,
  diffInfo = null,
  isPreviewMode = false
}) => {
  const [
    replaceRecordOverrideDialog,
    setReplaceRecordOverrideDialog
  ] = React.useState(false);
  const [
    recordOverrideDialogContent,
    setRecordOverrideDialogContent
  ] = React.useState({});
  const [value, setValue] = React.useState("");
  const [valueChanged, setValueChanged] = React.useState(false);
  const [sourceTimeStamp, setSourceTimeStamp] = React.useState(new Date());
  const [systemType, setSystemType] = React.useState("email");
  const [contactType, setContactType] = React.useState("PERBUSEMAIL|work");
  const [showAuditModal, setShowAuditModal] = React.useState(false);
  const [pendingOverrideDetails, setPendingOverrideDetails] = React.useState(
    null
  );

  if (isSummary) {
    return null;
  }

  const prepareOverrideOriginalValue = (telecom, type) => {
    const extensionMap = overrideSummaryForTelecom(telecom, type);
    oncRecordOverrideIconClick(extensionMap);
  };

  const actionHandler = detailsMap => {
    if (isMultipleAttributeOverride) {
      handleMultipleAttributeOverride(
        detailsMap,
        "telecom",
        attributeOverrides,
        setAttributeOverrides
      );
      setReplaceRecordOverrideDialog(false);
    } else {
      oncRecordOverrideIconClick(detailsMap);
    }
  };

  const handleReplaceOverride = detailsMap => {
    if (
      detailsMap["Mark as"] &&
      (detailsMap["Mark as"] === "Replace" || detailsMap["Mark as"] === "New")
    ) {
      if (detailsMap.originalFieldData) {
        setValue(
          detailsMap.originalFieldData.value
            ? detailsMap.originalFieldData.value
            : ""
        );
      }
      setSourceTimeStamp(
        detailsMap["Source Timestamp"] && detailsMap["Source Timestamp"] !== "-"
          ? new Date(detailsMap["Source Timestamp"])
          : new Date()
      );
      setRecordOverrideDialogContent(detailsMap);
      setReplaceRecordOverrideDialog(true);
    } else {
      actionHandler(detailsMap);
    }
  };

  const handleConfirm = () => {
    const detailsMap = { ...recordOverrideDialogContent };
    detailsMap.originalFieldData.value = value;
    let area = "",
      phExchange = "",
      phNumber = "",
      phCountry = "";
    if (systemType === "phone" || detailsMap["System"] === "Phone") {
      const digits = value.replace(/\D/g, "");
      phNumber = digits.slice(-4);
      phExchange = digits.slice(-7, -4);
      area = digits.slice(-10, -7);
      phCountry = digits.slice(0, -10);
    }
    if (
      detailsMap["Mark as"] === "Replace" &&
      detailsMap["System"] === "Phone"
    ) {
      if (!phCountry) {
        detailsMap.originalFieldData.extension = detailsMap.originalFieldData.extension.filter(
          ext => !ext.url.includes("contactpoint-country")
        );
      }
      detailsMap.originalFieldData.extension.forEach(item => {
        if (item.url.includes("contactpoint-area")) {
          item.valueString = area;
        } else if (item.url.includes("contactpoint-phExchange")) {
          item.valueString = phExchange;
        } else if (item.url.includes("contactpoint-phNumber")) {
          item.valueString = phNumber;
        } else if (item.url.includes("contactpoint-country")) {
          item.valueString = phCountry;
        }
      });
    }

    if (!detailsMap.originalFieldData.period)
      detailsMap.originalFieldData.period = {};
    detailsMap.originalFieldData.period.start = sourceTimeStamp.toISOString();
    if (detailsMap["Mark as"] === "New") {
      detailsMap.originalFieldData.use = contactType.split("|")[1];
      detailsMap.originalFieldData.extension = [
        {
          url:
            "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/imdm/#attrCode",
          valueString: contactType.split("|")[0]
        }
      ];
      if (systemType === "phone") {
        detailsMap.originalFieldData.extension = [
          ...detailsMap.originalFieldData.extension,
          {
            url: "http://hl7.org/fhir/StructureDefinition/contactpoint-area",
            valueString: area
          },
          {
            url:
              "http://hl7.org/fhir/StructureDefinition/contactpoint-phExchange",
            valueString: phExchange
          },
          {
            url:
              "http://hl7.org/fhir/StructureDefinition/contactpoint-phNumber",
            valueString: phNumber
          }
        ];
        if (phCountry) {
          detailsMap.originalFieldData.extension.push({
            url: "http://hl7.org/fhir/StructureDefinition/contactpoint-country",
            valueString: phCountry
          });
        }
      }
      detailsMap.originalFieldData.system = systemType;
    }

    if (isMultipleAttributeOverride)
      // Directly proceed to next step without audit modal
      actionHandler(detailsMap);
    else {
      setPendingOverrideDetails(detailsMap);
      setShowAuditModal(true);
    }
  };

  const handleAuditSubmit = auditInfo => {
    if (pendingOverrideDetails) {
      const updatedDetails = {
        ...pendingOverrideDetails,
        auditInfoList: auditInfo
      };
      actionHandler(updatedDetails);
      setPendingOverrideDetails(null);
    }
    setShowAuditModal(false);
  };

  const changeSystemType = e => {
    setSystemType(e.target.value);
    if (e.target.value === "email") {
      setContactType("PERBUSEMAIL|work");
    } else {
      setContactType("PERHOMEPHONE|home");
    }
  };

  const replaceOverrideModal = () => {
    return (
      <Modal
        open={replaceRecordOverrideDialog}
        onClose={() => setReplaceRecordOverrideDialog(false)}
      >
        <h3 style={{ textAlign: "center" }}>
          {recordOverrideDialogContent["Mark as"] !== "New" ? "Replace" : "New"}{" "}
          override
        </h3>
        <br />
        <br />
        <div>
          {recordOverrideDialogContent && (
            <table className={"recOvrTable"}>
              <tr>
                <td>System</td>
                <td>Value</td>
                {recordOverrideDialogContent["Mark as"] !== "New" && (
                  <td>Use</td>
                )}
                {recordOverrideDialogContent["Mark as"] !== "New" && (
                  <td>Attribute</td>
                )}
                <td>Source Timestamp</td>
                {recordOverrideDialogContent["Mark as"] === "New" && (
                  <td>Contact Type</td>
                )}
              </tr>
              <tr>
                {recordOverrideDialogContent["Mark as"] !== "New" && (
                  <td>
                    <input
                      type="text"
                      value={recordOverrideDialogContent["System"]}
                      disabled={true}
                    />
                  </td>
                )}
                {recordOverrideDialogContent["Mark as"] === "New" && (
                  <td>
                    <select value={systemType} onChange={changeSystemType}>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                    </select>
                  </td>
                )}
                <td>
                  <input
                    type="text"
                    value={value}
                    onChange={e => {
                      setValue(e.target.value);
                      if (!valueChanged) {
                        setValueChanged(true);
                      }
                    }}
                  />
                </td>
                {recordOverrideDialogContent["Mark as"] !== "New" && (
                  <td>
                    <input
                      type="text"
                      value={recordOverrideDialogContent["Use"]}
                      disabled={true}
                    />
                  </td>
                )}
                {recordOverrideDialogContent["Mark as"] !== "New" && (
                  <td>
                    <input
                      type="text"
                      value={recordOverrideDialogContent["Attribute"]}
                      disabled={true}
                    />
                  </td>
                )}
                <td>
                  <DateTimePicker
                    format="MM-dd-y hh:mm:ss a"
                    value={sourceTimeStamp}
                    onChange={changeDate => {
                      setSourceTimeStamp(changeDate);
                      if (!valueChanged) {
                        setValueChanged(true);
                      }
                    }}
                  />
                </td>
                {recordOverrideDialogContent["Mark as"] === "New" && (
                  <td>
                    <select
                      value={contactType}
                      onChange={e => setContactType(e.target.value)}
                    >
                      {systemType === "email" &&
                        listOfEmailTypes().map(type => {
                          return (
                            <option value={type.attrCode + "|" + type.use}>
                              {type.displayName}
                            </option>
                          );
                        })}
                      {systemType === "phone" &&
                        listOfPhoneTypes().map(type => {
                          return (
                            <option value={type.attrCode + "|" + type.use}>
                              {type.displayName}
                            </option>
                          );
                        })}
                    </select>
                  </td>
                )}
              </tr>
            </table>
          )}
        </div>
        <br />
        <br />
        <div style={{ marginTop: "1rem" }}>
          <ButtonList
            style={{
              display: "flex",
              justifyContent: "center"
            }}
          >
            <Button
              primary
              disabled={!valueChanged}
              onClick={() => handleConfirm()}
            >
              Confirm Changes
            </Button>
            <Button
              onClick={() => {
                setValueChanged(false);
                setReplaceRecordOverrideDialog(false);
              }}
            >
              Cancel
            </Button>
          </ButtonList>
        </div>
      </Modal>
    );
  };

  const addNewContactLink = () => {
    return (
      <LinkButton
        stickLeft={true}
        icon={"add"}
        onClick={() =>
          handleReplaceOverride({
            Field: "Contact Information",
            originalFieldData: {},
            "Mark as": "New",
            originalFieldName: "telecom"
          })
        }
        iconClassName={styles.addNewOverrodeIcon}
      >
        {"Add Contact Information"}
      </LinkButton>
    );
  };

  const UndeliverableInd = undelRsnTpCdExt => {
    switch (undelRsnTpCdExt.valueString) {
      case "6":
        return "6-Invalid Phone";
      case "7":
        return "7-Invalid Email";
      case "9":
        return "9-Deleted Phone";
      case "10":
        return "10-Deleted Email";
      default:
        return "-";
    }
  };

  if (data.length === 0) {
    return (
      <section>
        {replaceOverrideModal()}
        {isImdm &&
          oncRecordOverrideIconClick &&
          !isMultipleAttributeOverride &&
          !isInvisible &&
          !isNewQuarantine &&
          addNewContactLink()}

        {!oncRecordOverrideIconClick && (
          <>
            <h3>Contact Information</h3>
            <div>
              <h4>Empty</h4>
            </div>
          </>
        )}
      </section>
    );
  }

  return (
    <section>
      <OverrideAuditInputModal
        isOpen={showAuditModal}
        onCancel={() => setShowAuditModal(false)}
        onSubmit={handleAuditSubmit}
      />
      <div className={className + " " + styles.header}>
        {!isAggregateInfo && (
          <div
            className={
              isMultipleAttributeOverride
                ? styles.multipleAttributeOverrideColumn
                : styles.actionColumn
            }
          />
        )}
        <div>
          <h4>System</h4>
        </div>
        <div>
          <h4>Value</h4>
        </div>
        <div>
          <h4>Use</h4>
        </div>
        <div>
          <h4>Attribute</h4>
        </div>
        <div>
          <h4>Source Timestamp</h4>
        </div>
        {isAggregateInfo && (
          <div>
            <h4>Source</h4>
          </div>
        )}
        <div>
          <h4>Undeliverable</h4>
        </div>
      </div>

      {data.map((contactMethod, index) => {
        const attrCode = contactMethod.extension
          ? contactMethod.extension.filter(e => e.url.includes("attrCode"))
          : "";
        const sourceCode = contactMethod.extension
          ? contactMethod.extension.filter(e => e.url.includes("sourceCode"))
          : "";
        const undelRsnTpCdExt = contactMethod.extension
          ? contactMethod.extension.filter(
              e =>
                e.url.includes("#contactpoint-phUndelInd") ||
                e.url.includes("#email-undelRsnTpCd")
            )
          : "";
        
        // Get prior telecom data if available for diff comparison (only for use === "home")
        const isHomeTelecom = contactMethod.use === "home";
        const priorTelecom = (isPreviewMode && hasDiff && diffInfo && diffInfo.prior && diffInfo.prior[index] && isHomeTelecom) 
          ? diffInfo.prior[index] 
          : null;
        const changeType = (diffInfo && isHomeTelecom) ? diffInfo.changeType : null;

        const telecomMap = {
          Field: "Contact Information",
          System: contactMethod.system ? capitalize(contactMethod.system) : "-",
          Value: contactMethod.value ? contactMethod.value : "-",
          Use: contactMethod.use ? capitalize(contactMethod.use) : "-",
          Attribute: attrCode[0] ? attrCode[0].valueString : "-",
          Source: sourceCode[0] ? sourceCode[0].valueString : "-",
          Undeliverable: undelRsnTpCdExt[0]
            ? UndeliverableInd(undelRsnTpCdExt[0])
            : "-",
          "Source Timestamp":
            contactMethod.period && contactMethod.period.start
              ? formattedDateWithDefault(contactMethod.period.start)
              : "-",
          "Mark as": "",
          originalFieldName: "telecom",
          originalFieldData: contactMethod,
          originalFieldDataCopy: JSON.parse(JSON.stringify(contactMethod)),
          id: index
        };
        
        // Prepare prior values for diff display
        const priorTelecomMap = priorTelecom ? {
          System: priorTelecom.system ? capitalize(priorTelecom.system) : "-",
          Value: priorTelecom.value ? priorTelecom.value : "-",
          Use: priorTelecom.use ? capitalize(priorTelecom.use) : "-"
        } : null;

        return (
          <div key={index} className={`${className} ${hasDiff && isPreviewMode && isHomeTelecom ? 'preview-diff-highlight' : ''}`}>
            {!isAggregateInfo && (
              <div
                className={
                  isMultipleAttributeOverride
                    ? styles.multipleAttributeOverrideColumn
                    : styles.actionColumn
                }
                style={{ zIndex: 998 - index }}
              >
                {oncRecordOverrideIconClick &&
                  !isInvisible &&
                  !isNewQuarantine && (
                    <RecordOverrideOptions
                      detailsMap={telecomMap}
                      onClickRecordOverrideAction={detailsMap =>
                        handleReplaceOverride(detailsMap)
                      }
                      onClickRecordOverrideButton={type =>
                        prepareOverrideOriginalValue(
                          telecomMap.originalFieldData,
                          type
                        )
                      }
                      extension={
                        contactMethod.hasOwnProperty("extension")
                          ? contactMethod.extension.filter(
                              item =>
                                item.url ===
                                "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#override"
                            )
                          : []
                      }
                      isMultipleAttributeOverride={isMultipleAttributeOverride}
                      selectedOverrides={attributeOverrides.telecom}
                    />
                  )}
              </div>
            )}
            <div>
              {isHomeTelecom && changeType ? renderFieldWithDiff(
                telecomMap.System, 
                priorTelecomMap ? priorTelecomMap.System : null, 
                changeType,
                isPreviewMode,
                hasDiff
              ) : <div>{telecomMap.System}</div>}
            </div>
            <div>
              {isHomeTelecom && changeType ? renderFieldWithDiff(
                telecomMap.Value, 
                priorTelecomMap ? priorTelecomMap.Value : null, 
                changeType,
                isPreviewMode,
                hasDiff
              ) : <div>{telecomMap.Value}</div>}
            </div>
            <div>
              {isHomeTelecom && changeType ? renderFieldWithDiff(
                telecomMap.Use, 
                priorTelecomMap ? priorTelecomMap.Use : null, 
                changeType,
                isPreviewMode,
                hasDiff
              ) : <div>{telecomMap.Use}</div>}
            </div>
            <div>
              <div>{telecomMap["Attribute"]}</div>
            </div>
            <div>
              <div>{telecomMap["Source Timestamp"]}</div>
            </div>
            {isAggregateInfo && (
              <div>
                <div>{telecomMap["Source"]}</div>
              </div>
            )}
            <div>
              <div>{telecomMap["Undeliverable"]}</div>
            </div>
          </div>
        );
      })}
      {isImdm &&
        oncRecordOverrideIconClick &&
        !isInvisible &&
        !isNewQuarantine &&
        !isMultipleAttributeOverride &&
        addNewContactLink()}
      {replaceRecordOverrideDialog && replaceOverrideModal()}
    </section>
  );
};

export default Telecom;
